import { useCallback, useMemo, useState } from "react";
import { useSelector } from "store";

import { useShopifyLiveCommerceCart } from "../../features/shopifyLiveCommerce/cartStore";
import { clearShopifyLiveCommerceCart } from "../../features/shopifyLiveCommerce/syncCart";
import { useLiveCommerceProducts } from "../../features/shopifyLiveCommerce/useLiveCommerceProducts";
import { useShopifyCheckoutUrl } from "../../features/shopifyLiveCommerce/useShopifyCheckoutUrl";
import { selectLiveCommerceGoods } from "./liveCommerce/selectLiveCommerceGoods";

import type { ProductData } from "@spwn/types/firebase/firestore";
import type { Store } from "store";

/**
 * Shopify チェックアウト版ライブコマースの状態。
 * 商品・カート・checkoutUrl はいずれも Redux を通さずに持つ。
 */
export const useShopifyLiveCommerce = () => {
  const eventId = useSelector(
    (state: Store) => state.event.displayEvent
  )?.eventId;
  const isAdmin = useSelector((state: Store) => state.admin.isAdmin);
  const uid = useSelector((state: Store) => state.auth.user.uid);

  const products = useLiveCommerceProducts(eventId, Boolean(uid));
  const goods = useMemo(
    () => selectLiveCommerceGoods(products, isAdmin),
    [products, isAdmin]
  );

  const cart = useShopifyLiveCommerceCart(eventId);
  const checkoutUrl = useShopifyCheckoutUrl(eventId, cart.items);

  const { increment, addIfAbsent, clear } = cart;

  // 複数バリエーションの商品は、選ばれたバリエーションをカートに入れる
  const [selectType, setSelectType] = useState<string | null>(null);
  const getAddItem = useCallback(
    (list: ProductData[]): ProductData | undefined => {
      if (list.length === 1) return list[0];
      return selectType === null
        ? undefined
        : list.find((item) => item._id === selectType);
    },
    [selectType]
  );

  const addToCart = useCallback(
    (classGoodsList: ProductData[]) => {
      const item = getAddItem(classGoodsList);
      if (item !== undefined) increment(item);
    },
    [getAddItem, increment]
  );

  /** 「今すぐ購入」。カートに無ければ入れるだけで、購入は同期後にフッターから行う */
  const addToCartToBuy = useCallback(() => {
    const item = getAddItem(goods);
    if (item !== undefined) addIfAbsent(item);
  }, [getAddItem, goods, addIfAbsent]);

  /** 注文済みの Shopify カートを忘れてから、画面のカートを空にする */
  const clearAfterPurchase = useCallback(() => {
    if (eventId !== undefined) clearShopifyLiveCommerceCart(eventId);
    clear();
  }, [eventId, clear]);

  return {
    eventId,
    goods,
    cartItems: cart.items,
    cartTotalNum: cart.totalNum,
    checkoutUrl,
    incrementCartItem: cart.increment,
    decrementCartItem: cart.decrement,
    addToCart,
    addToCartToBuy,
    setSelectType,
    clearAfterPurchase,
  };
};
